/**
 * IntakeEventsFilters — panneau de filtres + export pour la section
 * « Soumissions reçues » de la page détail intake-link (Chunk 9.N.3.C).
 *
 * Décisions PO :
 *   - Q3=A always-visible desktop ≥ md (au-dessus de la liste)
 *   - Q4=A mobile < md drawer/bottom-sheet déclenché par bouton « Filtres »
 *   - Q5=A aucun filtre par défaut au mount
 *   - Q7=A export CSV cap 5000 + warning UI si X-Truncated
 *
 * UX :
 *   - status pills 6 options ("all" + 5 statuts DB) parité IntakeLinkStatusFilter
 *   - 2 inputs `type="date"` pour from/to (native picker — accessible mobile)
 *   - search input avec debounce 300ms (useEffect setTimeout)
 *   - bouton « Réinitialiser » visible uniquement si ≥1 filtre actif
 *   - bouton « Exporter CSV » disabled pendant export en cours OU si 0 résultat
 *
 * Anti-leak (PO correction post-Phase 0) :
 *   - L'export CSV passe par `api.download(path, filename)` qui :
 *     - utilise `fetch()` direct avec Authorization Bearer header (pas URL token)
 *     - lit la response en Blob (pas signed URL Storage)
 *     - `URL.createObjectURL` + anchor click + `revokeObjectURL` immédiat
 *   - 0 token / signed URL / payload jsonb / ip_hash / storage_path en URL,
 *     console, localStorage, sessionStorage. Erreurs propagées via ApiException.
 *
 * Brand alignment :
 *   - tokens Tailwind : brand-600 actif / slate-200 inactif / red-700 reset
 *   - focus-visible:ring-2 ring-brand-500 obligatoire (Vercel WIG)
 *   - Inter via classes Tailwind (typo neutre, pas de famille serif)
 *   - touch-manipulation hit area ≥ 44pt mobile
 */

import { useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { IntakeEventStatusFilter } from "../../../lib/queries/intakeLinks";

export type EventsFilterState = {
  status: IntakeEventStatusFilter;
  from: string; // ISO date "YYYY-MM-DD" ou ""
  to: string;
  q: string;
};

export const EMPTY_FILTERS: EventsFilterState = {
  status: "all",
  from: "",
  to: "",
  q: "",
};

const STATUS_OPTIONS: ReadonlyArray<IntakeEventStatusFilter> = [
  "all",
  "pending_classification",
  "pending_resolution",
  "resolved",
  "rejected",
  "superseded",
];

export function hasActiveFilter(f: EventsFilterState): boolean {
  return (
    f.status !== "all" ||
    f.from.trim() !== "" ||
    f.to.trim() !== "" ||
    f.q.trim().length >= 2
  );
}

/**
 * Hook custom — search debounce 300ms (Q1 PO « pas live à chaque caractère »).
 * Retourne le `q` débouncé prêt à être envoyé au backend (length ≥ 2 ou "").
 */
export function useDebouncedSearch(rawValue: string, delayMs = 300): string {
  const [debounced, setDebounced] = useState(rawValue);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(rawValue.trim()), delayMs);
    return () => clearTimeout(t);
  }, [rawValue, delayMs]);
  return debounced.length >= 2 ? debounced : "";
}

type Props = {
  /** État local des filtres (contrôlé par le parent — IntakeLinkDetailPage). */
  value: EventsFilterState;
  /** Callback à chaque changement de filtre — le parent gère la query refetch. */
  onChange: (next: EventsFilterState) => void;
  /** Callback bouton « Exporter CSV ». Le parent appelle `api.download(...)`. */
  onExport: () => void;
  /** Disabled state du bouton export (mutation in-flight ou 0 résultat). */
  exportDisabled?: boolean;
  /** Label optionnel pour aria-controls (id de la liste contrôlée). */
  controlsId?: string;
};

export function IntakeEventsFilters({
  value,
  onChange,
  onExport,
  exportDisabled,
  controlsId,
}: Props) {
  const { t } = useTranslation();
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const fromId = useId();
  const toId = useId();
  const qId = useId();

  const hasFilter = useMemo(() => hasActiveFilter(value), [value]);

  const reset = () => {
    onChange(EMPTY_FILTERS);
    setMobileDrawerOpen(false);
  };

  // Render — desktop always-visible / mobile drawer trigger button
  return (
    <>
      {/* Desktop ≥ md — always-visible panel */}
      <div
        className="hidden rounded-xl border border-slate-200 bg-white p-4 md:block"
        aria-label={t("intake_links.detail.events.filters.aria_label")}
      >
        <FilterPanelInner
          value={value}
          onChange={onChange}
          onReset={reset}
          onExport={onExport}
          exportDisabled={exportDisabled}
          hasFilter={hasFilter}
          controlsId={controlsId}
          fromId={fromId}
          toId={toId}
          qId={qId}
          t={t}
        />
      </div>

      {/* Mobile < md — bouton trigger + drawer slide-up */}
      <div className="md:hidden">
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => setMobileDrawerOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 touch-manipulation"
            aria-haspopup="dialog"
            aria-expanded={mobileDrawerOpen}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path
                d="M2 4h12M4 8h8M6 12h4"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
              />
            </svg>
            {t("intake_links.detail.events.filters.mobile_trigger")}
            {hasFilter && (
              <span
                className="ml-1 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-brand-600 px-1.5 text-xs font-medium text-white"
                aria-label={t("intake_links.detail.events.filters.active_badge")}
              >
                ●
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={onExport}
            disabled={exportDisabled}
            className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 touch-manipulation"
          >
            {t("intake_links.detail.events.filters.export_csv")}
          </button>
        </div>

        {mobileDrawerOpen && (
          <div
            role="dialog"
            aria-modal="true"
            aria-label={t("intake_links.detail.events.filters.aria_label")}
            className="fixed inset-0 z-50 flex items-end bg-slate-900/50 md:hidden"
            onClick={(e) => {
              if (e.target === e.currentTarget) setMobileDrawerOpen(false);
            }}
          >
            <div className="w-full rounded-t-2xl bg-white p-4 shadow-xl">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-base font-semibold text-slate-900">
                  {t("intake_links.detail.events.filters.mobile_title")}
                </h3>
                <button
                  type="button"
                  onClick={() => setMobileDrawerOpen(false)}
                  className="rounded-md p-1 text-slate-500 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 touch-manipulation"
                  aria-label={t("intake_links.detail.events.filters.mobile_close")}
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                    <path
                      d="M5 5l10 10M15 5L5 15"
                      stroke="currentColor"
                      strokeWidth="1.75"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </div>
              <FilterPanelInner
                value={value}
                onChange={onChange}
                onReset={reset}
                onExport={onExport}
                exportDisabled={exportDisabled}
                hasFilter={hasFilter}
                controlsId={controlsId}
                fromId={fromId}
                toId={toId}
                qId={qId}
                t={t}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Inner panel — partagé desktop/mobile drawer
// ---------------------------------------------------------------------------

type InnerProps = {
  value: EventsFilterState;
  onChange: (next: EventsFilterState) => void;
  onReset: () => void;
  onExport: () => void;
  exportDisabled?: boolean;
  hasFilter: boolean;
  controlsId?: string;
  fromId: string;
  toId: string;
  qId: string;
  t: (key: string) => string;
};

function FilterPanelInner({
  value,
  onChange,
  onReset,
  onExport,
  exportDisabled,
  hasFilter,
  controlsId,
  fromId,
  toId,
  qId,
  t,
}: InnerProps) {
  return (
    <div className="space-y-3">
      {/* Status pills */}
      <nav
        role="tablist"
        aria-label={t("intake_links.detail.events.filters.status_label")}
        className="flex gap-1.5 overflow-x-auto pb-1"
      >
        {STATUS_OPTIONS.map((opt) => {
          const selected = value.status === opt;
          return (
            <button
              key={opt}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-controls={controlsId}
              onClick={() => onChange({ ...value, status: opt })}
              className={[
                "shrink-0 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2",
                "touch-manipulation",
                selected
                  ? "border-brand-600 bg-brand-600 text-white"
                  : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
              ].join(" ")}
            >
              {t(`intake_links.detail.events.filters.status_options.${opt}`)}
            </button>
          );
        })}
      </nav>

      {/* Date range + search — grid sm:grid-cols-3 */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <label
            htmlFor={fromId}
            className="block text-xs font-medium text-slate-700"
          >
            {t("intake_links.detail.events.filters.date_from")}
          </label>
          <input
            id={fromId}
            type="date"
            value={value.from}
            onChange={(e) => onChange({ ...value, from: e.target.value })}
            className="mt-1 block w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          />
        </div>
        <div>
          <label
            htmlFor={toId}
            className="block text-xs font-medium text-slate-700"
          >
            {t("intake_links.detail.events.filters.date_to")}
          </label>
          <input
            id={toId}
            type="date"
            value={value.to}
            onChange={(e) => onChange({ ...value, to: e.target.value })}
            className="mt-1 block w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          />
        </div>
        <div>
          <label
            htmlFor={qId}
            className="block text-xs font-medium text-slate-700"
          >
            {t("intake_links.detail.events.filters.search_label")}
          </label>
          <input
            id={qId}
            type="search"
            inputMode="search"
            placeholder={t(
              "intake_links.detail.events.filters.search_placeholder",
            )}
            value={value.q}
            onChange={(e) => onChange({ ...value, q: e.target.value })}
            maxLength={100}
            spellCheck={false}
            autoComplete="off"
            className="mt-1 block w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          />
        </div>
      </div>

      {/* Actions footer — reset + export */}
      <div className="flex items-center justify-between gap-2 pt-1">
        {hasFilter ? (
          <button
            type="button"
            onClick={onReset}
            className="text-sm font-medium text-red-700 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 rounded-sm touch-manipulation"
          >
            {t("intake_links.detail.events.filters.reset")}
          </button>
        ) : (
          <span aria-hidden="true" />
        )}
        {/* Bouton export sur desktop — inline ici. Sur mobile il est en haut
            du drawer (déjà rendu par le parent). On utilise `hidden md:inline-flex`
            pour ne pas dupliquer en drawer mobile. */}
        <button
          type="button"
          onClick={onExport}
          disabled={exportDisabled}
          className="hidden items-center gap-1.5 rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 touch-manipulation md:inline-flex"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M8 2v8m0 0l-3-3m3 3l3-3M3 13h10"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {t("intake_links.detail.events.filters.export_csv")}
        </button>
      </div>
    </div>
  );
}
